import { Component, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { EditorComponent } from '@app/modules/shared/components/editor/editor.component'
import { UploadTypeEnum } from '@app/v2/auth/types/media.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { CityInputComponent } from '@app/v2/shared/components/inputs/city-input/city-input.component'
import { EmailInputComponent } from '@app/v2/shared/components/inputs/email-input/email-input.component'
import { PhoneInputComponent } from '@app/v2/shared/components/inputs/phone-input/phone-input.component'
import { UrlInputComponent } from '@app/v2/shared/components/inputs/url-input/url-input.component'
import { UploadComponent } from '@app/v2/shared/components/upload/upload.component'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { TranslateModule } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { debounceTime, Subject } from 'rxjs'
import { PublicInfoIcons } from '../../models/organization-settings.model'
import { OrganizationService } from '../../services/organization.service'
import { OrganizationSettings } from '../../types/organization-settings.type'
@Component({
  selector: 'app-public-info',
  imports: [
    TranslateModule,
    EmailInputComponent,
    ReactiveFormsModule,
    PhoneInputComponent,
    BasicInputComponent,
    UrlInputComponent,
    CityInputComponent,
    EditorComponent,
    NgxSkeletonLoaderModule,
    UploadComponent
  ],
  templateUrl: './public-info.component.html',
  styleUrl: './public-info.component.scss'
})
export class PublicInfoComponent implements OnInit {
  public organizationData = input<OrganizationSettings | null>(null)
  public isLoading = input<boolean>(true)
  public updateRequested = output<void>()
  public BasicInputMode = BasicInputMode
  public publicInfoFormGroup = signal<FormGroup | null>(null)
  public publicInfoIcons = PublicInfoIcons.getIconsConfiguration()
  public editorMessage = signal<string>('')

  private _blurSubject = new Subject<void>()
  private _destroyRef = inject(DestroyRef)
  private _userHasFinishedEditing = false
  private _previousEditorValue: string | null = null
  private _isEditorFocused = false
  public uploadTypeEnum = UploadTypeEnum
  constructor(private _organizationService: OrganizationService, private _authService: AuthService) {
    this._initializeForm()
    effect(() => {
      if (!this.isLoading() && this.organizationData()) this._getPublicInfo(this.organizationData())
    })

    // Debounce blur events to prevent duplicate update requests
    this._blurSubject.pipe(debounceTime(300), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this._userHasFinishedEditing && this.isFormDirty()) {
        this.updateRequested.emit()
        this._userHasFinishedEditing = false
      }
    })
  }

  ngOnInit(): void {
    this.publicInfoFormGroup()
      .controls.workspaceName.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        this._organizationService.organizationNameWhileEditing.set(value)
      })
  }

  private _initializeForm() {
    this.publicInfoFormGroup.set(
      new FormGroup({
        workspaceName: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
        companyEmail: new FormControl(null),
        companyPhone: new FormControl(null),
        companyWebsite: new FormControl(null, [Validators.maxLength(200)]),
        linkedinCompanyPage: new FormControl(null, [Validators.maxLength(200)]),
        city: new FormControl(null),
        companyDescription: new FormControl(null, [Validators.maxLength(500)]),
        organizationLogo: new FormGroup({
          path: new FormControl(null),
          id: new FormControl(null),
          name: new FormControl(null)
        })
      })
    )
  }

  private _getPublicInfo(data: OrganizationSettings): void {
    this.publicInfoFormGroup().controls.workspaceName.setValue(data.workspace_name)
    this.publicInfoFormGroup().controls.companyEmail.setValue(data.company_email)
    this.publicInfoFormGroup().controls.companyPhone.setValue(data.company_phone)
    this.publicInfoFormGroup().controls.companyWebsite.setValue(data.company_website)
    this.publicInfoFormGroup().controls.linkedinCompanyPage.setValue(data.linkedin_company_page)
    this.publicInfoFormGroup().controls.city.setValue(data.city)
    this.publicInfoFormGroup().controls.companyDescription.setValue(data.company_description)
    this.publicInfoFormGroup().controls.organizationLogo.get('path')?.setValue(this._authService.currentWorkspace?.logo)
    // Initialize the editor message and previous value
    this.editorMessage.set(data.company_description || '')
    this._previousEditorValue = data.company_description
  }

  /**
   * On blur event
   * @param {FocusEvent} event Event
   */
  public onBlur(event: FocusEvent): void {
    this._userHasFinishedEditing = true
    this._blurSubject.next()
  }

  /**
   * On child keydown
   * @param {KeyboardEvent} event Event
   */
  public onChildKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      // Route through blur subject so Enter + blur coalesce into one update
      if (!this._isEditorField(event)) {
        this._userHasFinishedEditing = true
        this._blurSubject.next()
      }
    }
  }

  /**
   * Check if the keyboard event is from the editor field
   * @param {KeyboardEvent} event Event
   * @returns {boolean} True if event is from editor
   */
  private _isEditorField(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement
    // Check if the event target is within the editor component
    return target.closest('ngx-editor') !== null || target.closest('.ProseMirror') !== null || target.classList.contains('ProseMirror')
  }

  /**
   * On description change from editor
   * @param {string} value New value
   */
  public onDescriptionChange(value: string): void {
    // Mark editor as focused when user is typing
    this._isEditorFocused = true

    // Normalize values for comparison (handle null/undefined vs empty string)
    const normalizedNewValue = value || ''
    const normalizedPreviousValue = this._previousEditorValue || ''

    // Only update if the value actually changed (not just focus)
    if (normalizedNewValue !== normalizedPreviousValue) {
      this.editorMessage.set(value)
      this.publicInfoFormGroup().controls.companyDescription.setValue(value, { emitEvent: false })
      this._previousEditorValue = value

      // Mark form as dirty but don't trigger update yet - only on blur
      this.publicInfoFormGroup().controls.companyDescription.markAsDirty()
    }
  }

  /**
   * On editor blur event
   * @param {string} value Editor value
   */
  public onEditorBlur(value: string): void {
    if (!this._isEditorFocused) return

    this._isEditorFocused = false

    // Ensure the form control is marked as dirty if content has changed
    const normalizedNewValue = value || ''
    const normalizedPreviousValue = this._previousEditorValue || ''

    if (normalizedNewValue !== normalizedPreviousValue) {
      this.publicInfoFormGroup().controls.companyDescription.setValue(value, { emitEvent: false })
      this.publicInfoFormGroup().controls.companyDescription.markAsDirty()
      this._previousEditorValue = value
    }

    this._userHasFinishedEditing = true
    this._blurSubject.next()
  }

  /**
   * On city value change (select/clear). Use blur subject so valueChanged + blur
   * from optionClicked do not fire update API twice.
   * @param {string} value New city value
   */
  public onCityValueChange(_value: string): void {
    this._userHasFinishedEditing = true
    this._blurSubject.next()
  }

  /**
   * Check if form has unsaved changes
   */
  public isFormDirty(): boolean {
    return this._organizationService.isFormDirty(this.publicInfoFormGroup()!)
  }

  /**
   * @description Gets skeleton theme colors based on current theme
   * @returns {object} Skeleton theme colors
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * @description On logo changed
   */
  public onLogoChanged(data): void {
    if (!data) return
    this._organizationService
      .updateOrganizationLogo({
        id: this.organizationData()?.workspace_id,
        logo: data.path
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Update localStorage with new logo using spread operator
          this._authService.currentWorkspace = {
            ...this._authService.currentWorkspace,
            logo: data.path
          }
        }
      })
  }

  /**
   * @description On logo deleted
   */
  public onLogoDeleted(): void {
    this._organizationService
      .updateOrganizationLogo({
        id: this.organizationData()?.workspace_id,
        logo: null
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Update localStorage with logo removed using spread operator
          this._authService.currentWorkspace = {
            ...this._authService.currentWorkspace,
            logo: null
          }
        }
      })
  }
}
